import { useQuery } from "@tanstack/react-query";
import { authApi, type PasswordPolicy } from "@repo/api";
import { useIntl } from "react-intl";

export function usePasswordPolicy() {
  return useQuery({
    queryKey: ["passwordPolicy"],
    queryFn: () => authApi.getPasswordPolicy(),
    staleTime: Infinity,
  });
}

export function validatePasswordWithPolicy(
  password: string,
  policy: PasswordPolicy,
): string[] {
  const errors: string[] = [];
  const minLen = policy.minLength ?? 8;
  const maxLen = policy.maxLength ?? 64;

  if (password.length < minLen || password.length > maxLen) {
    errors.push(`length:${minLen}:${maxLen}`);
  }
  if (
    policy.minUppercaseChars &&
    (password.match(/[A-Z]/g) ?? []).length < policy.minUppercaseChars
  ) {
    errors.push(`uppercase:${policy.minUppercaseChars}`);
  }
  if (
    policy.minLowercaseChars &&
    (password.match(/[a-z]/g) ?? []).length < policy.minLowercaseChars
  ) {
    errors.push(`lowercase:${policy.minLowercaseChars}`);
  }
  if (
    policy.minNumericChars &&
    (password.match(/[0-9]/g) ?? []).length < policy.minNumericChars
  ) {
    errors.push(`numeric:${policy.minNumericChars}`);
  }
  if (
    policy.minSpecialChars &&
    (password.match(/[^a-zA-Z0-9]/g) ?? []).length < policy.minSpecialChars
  ) {
    errors.push(`special:${policy.minSpecialChars}`);
  }
  return errors;
}

export function usePasswordErrors(
  password: string,
  policy: PasswordPolicy | undefined,
) {
  const intl = useIntl();

  if (!password || !policy) return [];

  const errorKeys = validatePasswordWithPolicy(password, policy);
  return errorKeys.map((key) => {
    const [type, ...args] = key.split(":");
    switch (type) {
      case "length":
        return intl.formatMessage(
          {
            id: "Al6t5D",
            defaultMessage: "{min}-{max} characters",
            description: "Password length requirement",
          },
          { min: args[0], max: args[1] },
        );
      case "uppercase":
        return intl.formatMessage(
          {
            id: "DS8yfp",
            defaultMessage: "At least {count} uppercase letter(s)",
            description: "Password uppercase requirement",
          },
          { count: args[0] },
        );
      case "lowercase":
        return intl.formatMessage(
          {
            id: "+NIB6n",
            defaultMessage: "At least {count} lowercase letter(s)",
            description: "Password lowercase requirement",
          },
          { count: args[0] },
        );
      case "numeric":
        return intl.formatMessage(
          {
            id: "nO0RDB",
            defaultMessage: "At least {count} number(s)",
            description: "Password numeric requirement",
          },
          { count: args[0] },
        );
      case "special":
        return intl.formatMessage(
          {
            id: "lLGXTD",
            defaultMessage: "At least {count} special character(s)",
            description: "Password special character requirement",
          },
          { count: args[0] },
        );
      default:
        return key;
    }
  });
}
